import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios';
import { ApiError } from '../types';
import { storage, ACCESS_TOKEN_KEY } from '../utils/storage';

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      timeout: 120000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // ✅ Request interceptor: attach token
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = storage.get<string>(ACCESS_TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    // ✅ Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // ✅ Handle unauthorized globally
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          // Avoid triggering logout / redirects for requests targeting auth endpoints
          const isAuthEndpoint = originalRequest.url?.includes('/auth/');
          if (!isAuthEndpoint) {
            this.handleAuthError();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private handleAuthError(): void {
    // Remove token from storage and notify app to handle logout centrally.
    storage.remove(ACCESS_TOKEN_KEY);
    // Emit a custom event so the auth layer can perform a single, coordinated logout
    // (avoids immediate redirect during app initialization).
    try {
      window.dispatchEvent(new Event('auth:logout'));
    } catch (err) {
      // Fallback to direct redirect if event dispatching is unavailable.
      window.location.href = '/login';
    }
  }

  public getInstance(): AxiosInstance {
    return this.instance;
  }
}

export const apiService = new ApiService();
export const api = apiService.getInstance();